'use client';

import { FC } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { ReadOnlyListing } from 'hotdesk_typescript';
import ListingCard from '../shared/ListingCard';
import HotdeskButton from '../shared/HotdeskButton';
import NoWorkspace from '../no-workspace/NoWorkspace';
import ExploreCardSkeletons from './ExploreCardSkeleton';
import type { CompareCardState } from '@/hotdesk-ui/hooks/compare/useExploreCompare';
import styles from './ExploreGrid.module.scss';

/** Placeholder cards shown while a fresh search is in flight. */
const SKELETON_CARDS = 9;
const LoadMoreLoading = dynamic(() => import('@/hotdesk-ui/components/loading/LoadMoreLoading'), {
  ssr: false
});
export interface ExploreGridProps {
  items: ReadOnlyListing[];
  total: number;
  allLoaded: boolean;
  loadingMore?: boolean;
  /** A page-1 fetch is in flight — the current results are already stale. */
  listRefreshing?: boolean;
  onLoadMore: () => void;
  isWorkspaceMode?: boolean;
  /** Compare mode state per card; returns undefined while compare mode is off. */
  compareCardState?: (item: ReadOnlyListing) => CompareCardState | undefined;
}
const ExploreGrid: FC<ExploreGridProps> = ({
  items,
  total,
  allLoaded,
  loadingMore,
  listRefreshing = false,
  onLoadMore,
  isWorkspaceMode = true,
  compareCardState
}) => {
  const t = useTranslations();

  // Nothing to show yet and more on the way — skeletons, not the empty
  // state. `total` is still the previous search's count here, so a search
  // run from a zero-result page would otherwise keep showing "no results".
  if (listRefreshing && items.length === 0) {
    return <div className={styles.gridWrap}>
                <div className={styles.grid}>
                    <ExploreCardSkeletons count={SKELETON_CARDS} />
                </div>
            </div>;
  }
  if (total === 0) {
    return <div className={styles.empty}>
                <NoWorkspace />
            </div>;
  }
  return <div className={styles.gridWrap} data-sentry-component="ExploreGrid" data-sentry-source-file="ExploreGrid.tsx">
            <div className={styles.grid}>
                {items.map((item, i) => <ListingCard key={`${item.id}-${i}`} listing={item as ReadOnlyListing} layout="explore" variant={isWorkspaceMode ? 'workspace' : 'listing'} priority={i < 2} compare={compareCardState?.(item)} />)}
            </div>

            {!allLoaded && total > items.length && <div className={styles.loadMoreRow}>
                    {loadingMore ? <LoadMoreLoading /> : <HotdeskButton variant="secondary" size="md" onClick={onLoadMore} className={styles.loadMoreBtn}>
                            {t('explore.button')}
                        </HotdeskButton>}
                </div>}
        </div>;
};
export default ExploreGrid;